import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ComposedChart, Scatter, Legend, Cell, Area, AreaChart } from 'recharts';
import '../styles/hl_buybacks.css';
import api_url from "../config/api_url.json";

console.log('api_url config:', api_url);

const base_api_url = api_url.backend_api;

console.log('base_api_url:', base_api_url);

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
};

const formatNumber = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(2)}K`;
  }
  return value.toFixed(2);
};

// Custom tooltip component matching the HyperliquidRiskMetrics design
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '12px',
        fontFamily: 'var(--font-family-base)',
      }}>
        <p style={{ margin: '0 0 4px 0', color: 'var(--color-text-secondary)' }}>
          Date: {label}
        </p>
        {payload.map((entry, index) => {
          let formattedValue;
          const value = entry.value;
          const name = entry.name;
          
          // Special handling for Average Buyback Price with amount
          if (name === 'Average Buyback Price' && entry.payload && entry.payload.buybackSize) {
            formattedValue = `${formatCurrency(value)} (${formatNumber(entry.payload.buybackSize)} HYPE)`;
          }
          // Format values based on the data type
          else if (name === 'USD Amount' || name === 'Average USD Amount' || name === 'Average Price' || name === 'Daily Close (USD)') {
            formattedValue = formatCurrency(value);
          } else if (name === 'HYPE Amount' || name === 'HYPE Balance' || name === 'Average HYPE Amount') {
            formattedValue = formatNumber(value);
          } else {
            formattedValue = value;
          }
          
          return (
            <p key={index} style={{ 
              margin: '4px 0 0 0', 
              color: entry.color, 
              fontWeight: '400',
              fontFamily: 'var(--font-family-mono)'
            }}>
              {name}: {formattedValue}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};


// Chart container wrapper
const ChartContainer = ({ children, title }) => (
  <div className="hl-buybacks-chart-container">
    <h3 className="hl-buybacks-buyback-chart-title">{title}</h3>
    <div className="hl-buybacks-chart-wrapper">
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  </div>
);

const HLBuybacks = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('week'); // 'week', 'twoWeek', 'month'
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [priceData, setPriceData] = useState([]);
  const [allocationData, setAllocationData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching from URL:', `${base_api_url}/api/data/hl-buybacks`);
        const response = await fetch(`${base_api_url}/api/data/hl-buybacks`);
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const jsonData = await response.json();
        console.log('jsonData:', jsonData);
        setData(jsonData.map(item => ({
          ...item,
          date: new Date(item.date).toISOString().split('T')[0],
          sz: parseFloat(item.sz),
          ntl: parseFloat(item.ntl),
          average_price: parseFloat(item.average_price)
        })));
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchPriceData = async () => {
      try {
        const response = await fetch(`${base_api_url}/api/data/hype-price`);
        if (!response.ok) {
          throw new Error('Failed to fetch price data');
        }
        const jsonData = await response.json();
        setPriceData(jsonData.map(item => ({
          ...item,
          date: new Date(item.date).toISOString().split('T')[0],
          close: parseFloat(item.close)
        })));
      } catch (error) {
        console.error('Error fetching price data:', error);
      }
    };

    fetchPriceData();
  }, []);

  useEffect(() => {
    const fetchAllocationData = async () => {
      try {
        const response = await fetch(`${base_api_url}/api/data/assistance-fund-holdings`);
        if (!response.ok) {
          throw new Error('Failed to fetch allocation data');
        }
        const jsonData = await response.json();
        setAllocationData(jsonData.map(item => ({
          date: new Date(item.datetime).toISOString().split('T')[0],
          balance: parseFloat(item.balance)
        })));
      } catch (error) {
        console.error('Error fetching allocation data:', error);
      }
    };

    fetchAllocationData();
  }, []);

  const calculateMetrics = (data) => {
    // Sort data by date in descending order to get most recent first
    const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));

    // Get the most recent day's data
    const mostRecentDate = sortedData.length > 0 ? sortedData[0].date : new Date().toISOString().split('T')[0];
    const dayData = sortedData.filter(item => item.date === mostRecentDate);

    const weekAgo = new Date(new Date(mostRecentDate).getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(new Date(mostRecentDate).getTime() - 30 * 24 * 60 * 60 * 1000);

    const weekData = sortedData.filter(item => new Date(item.date) >= weekAgo);
    const monthData = sortedData.filter(item => new Date(item.date) >= monthAgo);

    const calculateAverage = (items) => {
      if (items.length === 0) return 0;
      return items.reduce((sum, item) => sum + item.average_price, 0) / items.length;
    };

    return {
      dayUSD: dayData.reduce((sum, item) => sum + item.ntl, 0),
      dayHYPE: dayData.reduce((sum, item) => sum + item.sz, 0),
      dayAvgPrice: calculateAverage(dayData),
      weekUSD: weekData.reduce((sum, item) => sum + item.ntl, 0),
      weekHYPE: weekData.reduce((sum, item) => sum + item.sz, 0),
      weekAvgPrice: calculateAverage(weekData),
      monthUSD: monthData.reduce((sum, item) => sum + item.ntl, 0),
      monthHYPE: monthData.reduce((sum, item) => sum + item.sz, 0),
      monthAvgPrice: calculateAverage(monthData)
    };
  };

  const getChartData = () => {
    const timeframes = {
      week: 7,
      twoWeek: 14,
      month: 30,
      all: null // null means no filtering
    };

    const days = timeframes[timeframe];
    
    // If "all" is selected, return all data without filtering
    if (timeframe === 'all') {
      return data.sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - (days + 1));

    return data
      .filter(item => new Date(item.date) >= cutoffDate)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getChartDataWithAvgAmount = () => {
    const chartData = getChartData();
    if (chartData.length === 0) return [];

    // Calculate the average amount (sz) for the current chart data
    const avgAmount = chartData.reduce((sum, item) => sum + item.sz, 0) / chartData.length;

    // Add avgAmount to each data point (for a horizontal line)
    return chartData.map(item => ({
      ...item,
      avgAmount: avgAmount.toFixed(2)
    }));
  };

  const getChartDataWithAvgUSD = () => {
    const chartData = getChartData();
    if (chartData.length === 0) return [];

    // Calculate the average USD amount (ntl) for the current chart data
    const avgUSD = chartData.reduce((sum, item) => sum + item.ntl, 0) / chartData.length;

    // Add avgUSD to each data point (for a horizontal line)
    return chartData.map(item => ({
      ...item,
      avgUSD: (avgUSD).toFixed(2)
    }));
  };

  const getCombinedPriceData = () => {
    const buybackMap = new Map(data.map(item => [
      item.date,
      {
        price: item.average_price,
        amount: parseFloat(item.sz)  // Ensure it's a number
      }
    ]));

    return priceData.map(item => {
      const buybackInfo = buybackMap.get(item.date);
      return {
        date: item.date,
        price: item.close,
        buybackPrice: buybackInfo?.price || null,
        buybackSize: buybackInfo?.amount || null
      };
    });
  };

  const calculateDotSize = (amount) => {
    if (!amount) return 0;
    return Math.max(90, Math.min(750, amount / 375));
  };

  const getTickInterval = (dataLength) => {
    // For short timeframes (7D, 14D, 30D), show all ticks
    if (timeframe !== 'all') return 0;
    
    // For "All" timeframe, calculate appropriate interval to prevent overlapping ticks
    if (dataLength <= 30) return Math.floor(dataLength / 10); // Show ~10 ticks
    return Math.floor(dataLength / 15); // Show ~15 ticks for larger datasets
  };

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'asc'
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });
  }, [data, sortConfig]);

  const paginatedData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  if (loading) return <div className="hl-buybacks-loading">Loading buybacks data...</div>;
  if (error) return <div className="hl-buybacks-error">Error: {error}</div>;

  const metrics = calculateMetrics(data);

  return (
    <div className="hl-buybacks-dashboard">
      <h2 className="hl-buybacks-dashboard-title">HYPE Buybacks</h2>

      {/* Metrics Grid */}
      <div className="hl-buybacks-metrics-grid">
        <div className="hl-buybacks-metric-box">
          <h3>Past 24H Buyback in USD</h3>
          <p>{formatCurrency(metrics.dayUSD)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Past 24H Buyback in HYPE</h3>
          <p>{formatNumber(metrics.dayHYPE)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Average HYPE Buyback Price Past 24H</h3>
          <p>{formatCurrency(metrics.dayAvgPrice)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Past 7D Buyback in USD</h3>
          <p>{formatCurrency(metrics.weekUSD)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Past 7D Buyback in HYPE</h3>
          <p>{formatNumber(metrics.weekHYPE)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Average HYPE Buyback Price Past 7D</h3>
          <p>{formatCurrency(metrics.weekAvgPrice)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Past 30D Buyback in USD</h3>
          <p>{formatCurrency(metrics.monthUSD)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Past 30D Buyback in HYPE</h3>
          <p>{formatNumber(metrics.monthHYPE)}</p>
        </div>
        <div className="hl-buybacks-metric-box">
          <h3>Average HYPE Buyback Price Past 30D</h3>
          <p>{formatCurrency(metrics.monthAvgPrice)}</p>
        </div>
      </div>

      {/* Timeframe Selector */}
      <div className="hl-buybacks-timeframe-selector">
        <button
          className={`hl-buybacks-timeframe-button ${timeframe === 'week' ? 'active' : ''}`}
          onClick={() => setTimeframe('week')}
        >
          7D
        </button>
        <button
          className={`hl-buybacks-timeframe-button ${timeframe === 'twoWeek' ? 'active' : ''}`}
          onClick={() => setTimeframe('twoWeek')}
        >
          14D
        </button>
        <button
          className={`hl-buybacks-timeframe-button ${timeframe === 'month' ? 'active' : ''}`}
          onClick={() => setTimeframe('month')}
        >
          30D
        </button>
        <button
          className={`hl-buybacks-timeframe-button ${timeframe === 'all' ? 'active' : ''}`}
          onClick={() => setTimeframe('all')}
        >
          All
        </button>
      </div>

      {/* First Chart - USD Amount */}
      <ChartContainer title="Assistance Fund Buybacks (USD)">
        <ComposedChart
          data={getChartDataWithAvgUSD()}
          margin={{
            top: 40,
            right: 60,
            left: 60,
            bottom: 45
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            angle={-45}
            textAnchor="end"
            height={60}
            interval={getTickInterval(getChartData().length)}
            tickMargin={10}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            tickFormatter={formatCurrency}
            label={{
              value: 'USD Amount',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: 'var(--color-text-secondary)' },
              dx: -50
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            tickFormatter={formatCurrency}
            domain={[0, dataMax => Math.ceil(dataMax * 1.5)]}
            label={{
              value: 'Average Price',
              angle: 90,
              position: 'insideRight',
              style: { textAnchor: 'middle', fill: 'var(--color-text-secondary)' },
              dx: 50
            }}
          />
          <Tooltip
            content={<CustomTooltip />}
          />
          <Legend 
            verticalAlign="top" 
            height={36}
            wrapperStyle={{ 
              fontFamily: 'var(--font-family-base)', 
              fontSize: 'var(--font-size-xs)' 
            }}
          />
          <Bar
            yAxisId="left"
            dataKey="ntl"
            fill="#002475"
            name="USD Amount"
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="avgUSD"
            stroke="#008000"
            name="Average USD Amount"
            dot={false}
            strokeWidth={2}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="average_price"
            stroke="#ff7300"
            name="Average Price"
            dot={false}
            strokeWidth={2}
          />
        </ComposedChart>
      </ChartContainer>

      {/* Second Chart - HYPE Amount */}
      <ChartContainer title="Assistance Fund Buybacks (HYPE)">
        <ComposedChart
          data={getChartDataWithAvgAmount()}
          margin={{
            top: 40,
            right: 60,
            left: 60,
            bottom: 45
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            angle={-45}
            textAnchor="end"
            height={60}
            interval={getTickInterval(getChartData().length)}
            tickMargin={10}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            tickFormatter={formatNumber}
            label={{
              value: 'HYPE Amount',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: 'var(--color-text-secondary)' },
              dx: -50
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            tickFormatter={formatCurrency}
            domain={[0, dataMax => Math.ceil(dataMax * 1.5)]}
            label={{
              value: 'Average Price',
              angle: 90,
              position: 'insideRight',
              style: { textAnchor: 'middle', fill: 'var(--color-text-secondary)' },
              dx: 50
            }}
          />
          <Tooltip
            content={<CustomTooltip />}
          />
          <Legend 
            verticalAlign="top" 
            height={36}
            wrapperStyle={{ 
              fontFamily: 'var(--font-family-base)', 
              fontSize: 'var(--font-size-xs)' 
            }}
          />
          <Bar
            yAxisId="left"
            dataKey="sz"
            fill="#002475"
            name="HYPE Amount"
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="avgAmount"
            stroke="#008000"
            name="Average HYPE Amount"
            dot={false}
            strokeWidth={2}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="average_price"
            stroke="#ff7300"
            name="Average Price"
            dot={false}
            strokeWidth={2}
          />
        </ComposedChart>
      </ChartContainer>

      {/* Price Chart */}
      <div className="hl-buybacks-price-chart-container">
        <h3 className="hl-buybacks-price-chart-title">Assistance Fund Buys vs Market Price</h3>
        <div className="hl-buybacks-chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={getCombinedPriceData()}
              margin={{
                top: 40,
                right: 60,
                left: 60,
                bottom: 25
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={14}
                tickMargin={10}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                tickFormatter={formatCurrency}
                label={{
                  value: 'Price (USD)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: 'var(--color-text-secondary)' },
                  dx: -50
                }}
              />
              <Tooltip
                content={<CustomTooltip />}
              />
              <Legend
                verticalAlign="top"
                height={36}
                wrapperStyle={{ 
                  fontFamily: 'var(--font-family-base)', 
                  fontSize: 'var(--font-size-xs)' 
                }}
              />
              <Line
                type="monotone"
                dataKey="price"
                stroke="#002475"
                dot={false}
                name="Daily Close (USD)"
                strokeWidth={2}
              />
              <Scatter
                dataKey="buybackPrice"
                fill="#ff7300"
                opacity='80%'
                name="Average Buyback Price"
              >
                {
                  getCombinedPriceData().map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      size={calculateDotSize(entry.buybackSize)}
                    />
                  ))
                }
              </Scatter>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table */}
      <div className="hl-buybacks-table-container">
        <h3 className="hl-buybacks-table-title">Assistance Fund Buybacks (USD)</h3>
        <table className="hl-buybacks-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('date')}>
                Date
                <span className="hl-buybacks-sort-indicator">
                  {getSortIndicator('date')}
                </span>
              </th>
              <th onClick={() => handleSort('sz')}>
                Size (HYPE)
                <span className="hl-buybacks-sort-indicator">
                  {getSortIndicator('sz')}
                </span>
              </th>
              <th onClick={() => handleSort('ntl')}>
                Notional Amount Bought (USD)
                <span className="hl-buybacks-sort-indicator">
                  {getSortIndicator('ntl')}
                </span>
              </th>
              <th onClick={() => handleSort('average_price')}>
                Average Price
                <span className="hl-buybacks-sort-indicator">
                  {getSortIndicator('average_price')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={index}>
                <td>{row.date}</td>
                <td>{formatNumber(row.sz)}</td>
                <td>{formatCurrency(row.ntl)}</td>
                <td>{formatCurrency(row.average_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="hl-buybacks-pagination">
          <button
            className="hl-buybacks-pagination-button"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="hl-buybacks-pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="hl-buybacks-pagination-button"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Area Chart */}
      <div className="hl-buybacks-area-chart-container">
        <h3 className="hl-buybacks-area-chart-title">Assistance Fund HYPE Balance</h3>
        <div className="hl-buybacks-chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={allocationData}
              margin={{
                top: 40,
                right: 60,
                left: 60,
                bottom: 45
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={14}
                tickMargin={10}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                tickFormatter={formatNumber}
                label={{
                  value: 'HYPE Balance',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: 'var(--color-text-secondary)' },
                  dx: -50
                }}
              />
              <Tooltip
                content={<CustomTooltip />}
              />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#002475"
                fill="#002475"
                fillOpacity={0.3}
                strokeWidth={2}
                name="HYPE Balance"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default HLBuybacks; 